import type { ClassifiedDomain, CreatedIndustry } from "../../CustomHooks/customIndustryAgent.types";
import { AGENT_STEPS } from "../../CustomHooks/customIndustryAgent.types";

export const stepIndexOf = (data: unknown): number => {
    if (!data || typeof data !== "object") return -1;

    return AGENT_STEPS.findIndex((step) => step in (data as Record<string, unknown>));
};

const domainListFromEntries = (entries: unknown): ClassifiedDomain[] | null => {
    if (!Array.isArray(entries)) return null;

    return entries
        .filter((entry): entry is Record<string, unknown> => !!entry && typeof entry === "object")
        .map((entry) => ({
            domain: typeof entry.domain === "string" ? entry.domain : "",
            reason: typeof entry.reason === "string" ? entry.reason : "",
            avgMonthlyVisits: typeof entry.avg_monthly_visits === "number" ? entry.avg_monthly_visits : 0,
        }))
        .filter((entry) => entry.domain);
};

export const classifiedDomainsFromUpdate = (data: unknown): ClassifiedDomain[] | null => {
    if (!data || typeof data !== "object") return null;
    const update = (data as Record<string, unknown>).classify_domains;
    if (!update || typeof update !== "object") return null;

    return domainListFromEntries((update as Record<string, unknown>).full_domains);
};

// `normalize_topic` refines the raw description into a clean one-line topic — this is what
// lets its checklist row show real content instead of sitting there as a bare label.
export const topicSummaryFromUpdate = (data: unknown): string | null => {
    if (!data || typeof data !== "object") return null;
    const update = (data as Record<string, unknown>).normalize_topic;
    if (!update || typeof update !== "object") return null;

    const { topic_description: topicDescription, topic } = update as Record<string, unknown>;
    if (typeof topicDescription === "string" && topicDescription) return topicDescription;
    if (typeof topic === "string" && topic) return topic;
    return null;
};

// The agent reports a failure as `{ domain, error }`, and only the domain is shown: its
// `error` carries raw tool payloads and exception text, which is Sentry's business, not the
// user's.
export const trafficFailuresFromUpdate = (data: unknown): string[] | null => {
    if (!data || typeof data !== "object") return null;
    const update = (data as Record<string, unknown>).verify_traffic;
    if (!update || typeof update !== "object") return null;

    const failures = (update as Record<string, unknown>).failures;
    if (!Array.isArray(failures) || failures.length === 0) return null;

    const cleaned = failures
        .filter((failure): failure is Record<string, unknown> => !!failure && typeof failure === "object")
        .map((failure) => (typeof failure.domain === "string" ? failure.domain : ""))
        .filter((domain) => !!domain);

    return cleaned.length > 0 ? cleaned : null;
};

// `classify_domains` is where the segment budget is spent: anything the budget could not
// cover is reported here, having joined the industry as a whole domain instead.
export const segmentsOverBudgetFromUpdate = (data: unknown): number | null => {
    if (!data || typeof data !== "object") return null;
    const update = (data as Record<string, unknown>).classify_domains;
    if (!update || typeof update !== "object") return null;

    const overBudget = (update as Record<string, unknown>).segments_over_budget;
    return Array.isArray(overBudget) ? overBudget.length : null;
};

export const createdSegmentsFromUpdate = (data: unknown): ClassifiedDomain[] | null => {
    if (!data || typeof data !== "object") return null;
    const update = (data as Record<string, unknown>).create_segments;
    if (!update || typeof update !== "object") return null;

    return domainListFromEntries((update as Record<string, unknown>).segments);
};

// `create_segments` fans out into one `generate_rules` node per candidate domain before it
// reports its own final result, so this is the only live signal available for what's easily
// the longest-running, most opaque part of the run.
export const isGenerateRulesUpdate = (data: unknown): boolean => {
    if (!data || typeof data !== "object") return false;
    return "generate_rules" in (data as Record<string, unknown>);
};

export const runIdFromMetadata = (data: unknown): string | null => {
    if (!data || typeof data !== "object") return null;

    const runId = (data as Record<string, unknown>).run_id;
    return typeof runId === "string" && runId ? runId : null;
};

export const industryFromState = (data: unknown): CreatedIndustry | null => {
    if (!data || typeof data !== "object") return null;
    const state = data as Record<string, unknown>;
    const industryId = state.industry_id;
    const industryName = state.industry_name;

    if (typeof industryId !== "string" || !industryId) return null;

    return {
        industryId,
        industryName: typeof industryName === "string" ? industryName : "",
    };
};

/** The code the agent stopped the run with, or null while its state carries none (the agent
 *  writes `""` until then). */
export const errorCodeFromState = (data: unknown): string | null => {
    if (!data || typeof data !== "object") return null;

    const errorCode = (data as Record<string, unknown>).error_code;
    return typeof errorCode === "string" && errorCode ? errorCode : null;
};
